import { useEffect } from 'react';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import useAppParams from 'src/hooks/useAppParams';
import useAlertNotify from 'src/components/Toasts/AlertNotifications/hooks/useAlertNotify';
import useModal from 'src/components/Modals/hooks/useModal';
import { NotificationData } from 'src/utils/notifications';

/**
 * Only build an action when there's a real link to send the user to, so alerts
 * without one don't render an empty arrow button.
 */
function getLinkAction(
	linkText: string | undefined,
	linkUrl: string | undefined
): NotificationData['action'] {
	if (!linkText || !linkUrl) {
		return undefined;
	}

	return { type: 'link', label: linkText, href: linkUrl };
}

export default function useAppParamsAlertNotificationListener() {
	const appParams = useAppParams();
	const alertNotify = useAlertNotify();

	// For collateral alert.
	const spotMarkets = useSpotMarkets();
	const { show } = useModal();

	const appParamsAlert = appParams?.alert;

	const isAlertExpired =
		appParamsAlert &&
		appParamsAlert.alertData.untilMs &&
		appParamsAlert.alertData.untilMs < Date.now();

	useEffect(() => {
		if (!appParamsAlert?.alertType || isAlertExpired) {
			return;
		}

		switch (appParamsAlert.alertType) {
			case 'feature': {
				if (
					!appParamsAlert.alertData.heading ||
					!appParamsAlert.alertData.description
				) {
					return;
				}

				const {
					heading,
					description,
					linkText,
					linkUrl,
					imageSrc,
					disableAutoDismiss,
				} = appParamsAlert.alertData;

				alertNotify({
					id: `feature-alert-${heading}`,
					// TODO: We don't have image uploads implemented in the BE so keeping
					// this `minor` for now in order to mitigate any mistakes. Need to update
					// to `major` once BE is complete.
					alertType: 'minor',
					imageSrc,
					message: heading,
					description,
					disableAlertAutoDismiss: disableAutoDismiss,
					action: getLinkAction(linkText, linkUrl),
				});

				return;
			}
			case 'warning': {
				if (
					!appParamsAlert.alertData.heading ||
					!appParamsAlert.alertData.description
				) {
					return;
				}

				const { heading, description, linkText, linkUrl, disableAutoDismiss } =
					appParamsAlert.alertData;

				alertNotify({
					id: `warning-alert-${heading}`,
					alertType: 'minor',
					message: heading,
					description,
					disableAlertAutoDismiss: disableAutoDismiss,
					action: getLinkAction(linkText, linkUrl),
				});

				return;
			}
			case 'market': {
				if (!appParamsAlert.alertData.baseSymbol) {
					return;
				}

				const { baseSymbol, disableAutoDismiss } = appParamsAlert.alertData;

				alertNotify({
					id: `market-alert-${baseSymbol}`,
					alertType: 'minor',
					disableAlertAutoDismiss: disableAutoDismiss,
					message: `${baseSymbol} has landed!`,
					description: `Start trading ${baseSymbol}-PERP today!`,
					action: {
						type: 'link',
						label: 'Trade now',
						href: `/${baseSymbol}-PERP`,
					},
				});

				return;
			}
			case 'collateral': {
				if (!appParamsAlert.alertData.baseSymbol) {
					return;
				}

				const { baseSymbol, disableAutoDismiss } = appParamsAlert.alertData;
				const spotMarket = spotMarkets.find(
					(market) => market.symbol === baseSymbol
				);

				alertNotify({
					id: `collateral-alert-${baseSymbol}`,
					alertType: 'minor',
					disableAlertAutoDismiss: disableAutoDismiss,
					message: `${baseSymbol} is now live!`,
					description: `You can now deposit ${baseSymbol}.`,
					action: {
						label: `Deposit`,
						callback: () => {
							show({
								type: 'deposit',
								props: {
									collateralIndex: spotMarket?.marketIndex ?? 0,
								},
							});
						},
					},
				});

				return;
			}
			default:
				return;
		}
	}, [appParamsAlert, isAlertExpired, show, spotMarkets, alertNotify]);
}
